import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Auction } from '../types/auction.types';
import { sortSalesForListing } from '../utils/currentSale';
import { format } from 'date-fns';

const Dashboard: React.FC = () => {
  const { userProfile } = useAuth();
  const [auctions, setAuctions] = useState<Auction[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAuctions();
  }, [userProfile]);

  const loadAuctions = async () => {
    if (!userProfile) return;

    try {
      // 🔴 The orderBy('createdAt','desc') that used to be on this query is gone.
      // Firestore does not sort documents missing the ordered field to the end —
      // it OMITS them, so any sale without a createdAt was absent from this list
      // entirely, with no empty row and no count to reveal it. On the pages a
      // seller uses to see what sales exist, that is the worst place for it.
      //
      // The where() filter stays (it is what scopes the list to this auction
      // house). Ordering moves in-memory onto a key that is actually present:
      // the real end of the sale. As a side effect the query no longer needs a
      // composite index.
      const auctionsRef = collection(db, 'auctions');
      const q = query(
        auctionsRef,
        where('auctionHouseId', '==', userProfile.auctionHouseId),
      );

      const snapshot = await getDocs(q);
      const auctionsList: Auction[] = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          startDate: data.startDate?.toDate(),
          endDate: data.endDate?.toDate(),
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
        } as Auction;
      });

      // Newest real end first, drafts included — a list must never drop a sale,
      // only order it. Shared with SalePicker so all three agree.
      setAuctions(sortSalesForListing(auctionsList));
    } catch (error) {
      console.error('Error loading auctions:', error);
    } finally {
      setLoading(false);
    }
  };

  const stats = {
    total: auctions.length,
    draft: auctions.filter((a) => a.status === 'draft').length,
    scheduled: auctions.filter((a) => a.status === 'scheduled').length,
    live: auctions.filter((a) => a.status === 'live').length,
    ended: auctions.filter((a) => a.status === 'ended').length,
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Dashboard</h1>
          <p className="text-gray-600 mt-1">Welcome back, {userProfile?.auctionHouseName}!</p>
        </div>
        <Link to="/import" className="btn-primary">
          Import HiBid CSV
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid md:grid-cols-4 gap-6">
        <div className="card">
          <p className="text-gray-600 mb-2">Total Auctions</p>
          <p className="text-3xl font-bold text-primary-600">{stats.total}</p>
        </div>
        <div className="card">
          <p className="text-gray-600 mb-2">Draft</p>
          <p className="text-3xl font-bold text-gray-600">{stats.draft}</p>
        </div>
        <div className="card">
          <p className="text-gray-600 mb-2">Scheduled</p>
          <p className="text-3xl font-bold text-blue-600">{stats.scheduled}</p>
        </div>
        <div className="card">
          <p className="text-gray-600 mb-2">Live</p>
          <p className="text-3xl font-bold text-green-600">{stats.live}</p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card">
        <h2 className="text-xl font-bold mb-4">Quick Actions</h2>
        <div className="grid md:grid-cols-3 gap-4">
          <Link
            to="/import"
            className="p-4 border rounded-lg hover:bg-gray-50 transition"
          >
            <div className="text-2xl mb-2">📤</div>
            <h3 className="font-semibold">Import HiBid CSV</h3>
            <p className="text-sm text-gray-600">Bulk upload auction catalog</p>
          </Link>
          <Link
            to="/auctions"
            className="p-4 border rounded-lg hover:bg-gray-50 transition"
          >
            <div className="text-2xl mb-2">📋</div>
            <h3 className="font-semibold">View All Auctions</h3>
            <p className="text-sm text-gray-600">Manage your auction catalog</p>
          </Link>
          <div className="p-4 border rounded-lg opacity-50">
            <div className="text-2xl mb-2">⚙️</div>
            <h3 className="font-semibold">Settings</h3>
            <p className="text-sm text-gray-600">Coming soon...</p>
          </div>
        </div>
      </div>

      {/* Recent Auctions */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold">Recent Auctions</h2>
          <Link
            to="/auctions"
            className="text-primary-600 hover:text-primary-700 font-semibold"
          >
            View All →
          </Link>
        </div>

        {loading ? (
          <p className="text-center text-gray-600">Loading...</p>
        ) : auctions.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600 mb-4">No auctions yet</p>
            <Link to="/import" className="btn-primary">
              Import Your First Auction
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {auctions.slice(0, 5).map((auction) => (
              <div
                key={auction.id}
                className="border rounded-lg p-4 hover:bg-gray-50 transition"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="font-bold text-lg">{auction.title}</h3>
                    <p className="text-sm text-gray-600">
                      {format(auction.startDate, 'MMM dd, yyyy')} •{' '}
                      {auction.totalLots} lots
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <span
                      className={`px-3 py-1 rounded-full text-sm font-semibold ${
                        auction.status === 'live'
                          ? 'bg-green-100 text-green-800'
                          : auction.status === 'scheduled'
                          ? 'bg-blue-100 text-blue-800'
                          : auction.status === 'draft'
                          ? 'bg-gray-100 text-gray-800'
                          : 'bg-purple-100 text-purple-800'
                      }`}
                    >
                      {auction.status}
                    </span>
                    <Link
                      to={`/auctions/${auction.id}`}
                      className="btn-secondary"
                    >
                      View
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;